import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import axios, { AxiosError } from 'axios';
import { useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTokenManagement } from '..';
import { CONSTANTS } from '../../constants';
import { i18n } from '../../localization';
import { AuthContext } from '../../providers';
import {
  AuthenticatedUser,
  CognitoUserAttributes,
  CognitoUserWithChallengeProps,
  SSOSessionData,
  WindowEnv,
} from '../../types';
import { clearSSOStorage } from '../../utils/authErrorUtils';

// Type definitions for AWS Amplify internals
interface AmplifyAuth {
  userPool?: AmazonCognitoIdentity.CognitoUserPool;
  storage: AmplifyStorage;
  _storage?: AmplifyStorage;
}

interface AmplifyStorage {
  setItem: (key: string, value: string) => void;
  getItem: (key: string) => string | null;
  removeItem: (key: string) => void;
  clear?: () => void;
}

interface CognitoTokenPayload {
  username?: string;
  'cognito:username'?: string;
  [key: string]: unknown;
}

interface CognitoTokenWithPayload {
  payload?: CognitoTokenPayload;
}

interface TokenExchangeResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
}

interface TokenExchangeError {
  error?: string;
  error_description?: string;
}

/**
 * Custom hook for handling SSO login operations
 * Manages SSO session data, error detection, and IdP login flow
 *
 * @returns {Object} SSO login handler functions and utilities
 */
export const useSSOLoginHandler = () => {
  const { search } = useLocation();
  const { setUser } = useContext(AuthContext);
  const { saveIdToken } = useTokenManagement();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const ssoErrors = i18n.pages.login.ssoSignInErrors.errorPage;

  const userPoolConfig = useMemo(
    () => ({
      UserPoolId:
        (window as Window & { __env__?: WindowEnv }).__env__?.cognito
          .cognito_user_pool ??
        process.env.REACT_APP_COGNITO_USER_POOL ??
        '',
      ClientId:
        (window as Window & { __env__?: WindowEnv }).__env__?.cognito
          .userPoolWebClientId ??
        process.env.REACT_APP_COGNITO_WEB_CLIENT_ID ??
        '',
    }),
    [],
  );

  const errorDescription = useMemo(
    () => params.get('error_description') || '',
    [params],
  );

  /**
   * Ensures Amplify storage is properly configured
   * Creates a wrapper around localStorage if needed
   */
  const ensureAuthStorage = useCallback(
    (userPoolConfig: {
      UserPoolId: string;
      ClientId: string;
    }): AmplifyStorage => {
      const hasValidShape = (s: unknown): s is AmplifyStorage =>
        s !== null &&
        typeof s === 'object' &&
        'setItem' in s &&
        'getItem' in s &&
        'removeItem' in s &&
        typeof (s as AmplifyStorage).setItem === 'function' &&
        typeof (s as AmplifyStorage).getItem === 'function' &&
        typeof (s as AmplifyStorage).removeItem === 'function';

      let storage = (Auth as unknown as AmplifyAuth).storage;

      if (!hasValidShape(storage)) {
        // Create localStorage wrapper that matches Amplify's expected interface
        const ls = window.localStorage;
        const storageWrapper: AmplifyStorage = {
          setItem: (key: string, value: string) => {
            try {
              ls.setItem(key, value);
            } catch (e) {
              console.warn('[Storage] Failed to set item:', e);
            }
          },
          getItem: (key: string) => {
            try {
              return ls.getItem(key);
            } catch (e) {
              console.warn('[Storage] Failed to get item:', e);
              return null;
            }
          },
          removeItem: (key: string) => {
            try {
              ls.removeItem(key);
            } catch (e) {
              console.warn('[Storage] Failed to remove item:', e);
            }
          },
          clear: () => {
            try {
              const prefix = `CognitoIdentityServiceProvider.${userPoolConfig.ClientId}`;
              const keysToRemove: string[] = [];
              for (let i = 0; i < ls.length; i++) {
                const key = ls.key(i);
                if (key?.startsWith(prefix)) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach((key) => ls.removeItem(key));
            } catch (e) {
              console.warn('[Storage] Failed to clear:', e);
            }
          },
        };

        storage = storageWrapper;
        (Auth as unknown as AmplifyAuth).storage = storage;
        (Auth as unknown as AmplifyAuth)._storage = storage;

        // Reconfigure Auth with our storage wrapper
        Auth.configure({
          userPoolId: userPoolConfig.UserPoolId,
          region:
            (window as Window & { __env__?: WindowEnv }).__env__?.cognito
              .cognito_region ??
            process.env.REACT_APP_COGNITO_REGION ??
            'us-west-2',
          userPoolWebClientId: userPoolConfig.ClientId,
          storage: storage as unknown as Storage,
        });
      }

      return storage;
    },
    [],
  );

  /**
   * Checks for SSO errors in URL parameters
   * Detects new user scenarios and other SSO-related errors
   */
  const checkSsoError = useCallback(() => {
    const d = errorDescription.toLowerCase();
    const errorParam = params.get('error');

    if (!d && !errorParam) return { type: '', message: '' };

    // Ignore non-SSO error types (e.g., MFA session expired from useMfaSessionValidation)
    const nonSsoErrors = ['mfa_session_expired'];
    if (errorParam && nonSsoErrors.includes(errorParam)) {
      return { type: '', message: '' };
    }

    // New user detection patterns
    const newUserPatterns = [
      'no account linked',
      'user not found',
      'user does not exist',
      'no user found',
      'account not found',
      'user is not authorized',
      'user has not been setup',
    ];

    if (
      newUserPatterns.some((pattern) => d.includes(pattern)) ||
      (errorParam === 'invalid_request' && d.includes('no account'))
    ) {
      return { type: 'newUser', message: '' };
    }

    // Existing user with issues
    if (d.includes('already found an entry for username')) {
      return { type: 'ssoError', message: ssoErrors.generalError };
    }

    // Access denied patterns
    if (d.includes('access is denied') || d.includes('access denied')) {
      return { type: 'ssoError', message: ssoErrors.roleError };
    }

    // SAML issues
    if (d.includes('invalid saml response') || d.includes('saml')) {
      return { type: 'ssoError', message: ssoErrors.generalError };
    }

    // Authorization issues
    if (d.includes('not authorized') || d.includes('unauthorized')) {
      return { type: 'ssoError', message: ssoErrors.roleError };
    }

    // Catch-all for any other errors
    if (errorParam || d) {
      return { type: 'ssoError', message: ssoErrors.generalError };
    }

    return { type: '', message: '' };
  }, [errorDescription, params, ssoErrors]);

  /**
   * Exchanges OAuth authorization code for Cognito tokens
   */
  const getSessionData =
    useCallback(async (): Promise<SSOSessionData | null> => {
      const code = params.get('code');
      if (!code) {
        return null;
      }

      const {
        SSO: { domain, client_id, redirect_uri },
      } = CONSTANTS;

      try {
        const body = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id,
          code,
          redirect_uri,
        });

        const response = await axios.post<TokenExchangeResponse>(
          `https://${domain}/oauth2/token`,
          body,
          {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            timeout: 10000, // 10 second timeout
          },
        );

        const { access_token, id_token, refresh_token } = response.data;

        if (!access_token || !id_token) {
          throw new Error('Missing required tokens from Cognito response');
        }

        return {
          AccessToken: new AmazonCognitoIdentity.CognitoAccessToken({
            AccessToken: access_token,
          }),
          IdToken: new AmazonCognitoIdentity.CognitoIdToken({
            IdToken: id_token,
          }),
          RefreshToken: new AmazonCognitoIdentity.CognitoRefreshToken({
            RefreshToken: refresh_token ?? '',
          }),
        };
      } catch (error: unknown) {
        console.error('[getSessionData] Failed to get session data:', error);

        const axiosError = error as AxiosError<TokenExchangeError>;
        const errorData = axiosError.response?.data;

        if (
          errorData?.error === 'invalid_grant' ||
          errorData?.error_description?.toLowerCase().includes('user not found')
        ) {
          sessionStorage.setItem('sso_new_user', 'true');
        }

        // Clean up any stale auth data
        clearSSOStorage();
        return null;
      }
    }, [params]);

  /**
   * Fetches user attributes from Cognito user
   */
  const fetchUserAttributes = useCallback(
    (
      cognitoUser: AmazonCognitoIdentity.CognitoUser,
    ): Promise<CognitoUserAttributes> =>
      new Promise((resolve, reject) => {
        cognitoUser.getUserAttributes((err, result) => {
          if (err) {
            console.error('[fetchUserAttributes] Error:', err);
            reject(err);
          } else {
            const attributes: CognitoUserAttributes = {};
            result?.forEach((element) => {
              attributes[element.Name] = element.Value;
            });
            resolve(attributes);
          }
        });
      }),
    [],
  );

  /**
   * Installs session into Amplify storage so Auth.currentSession() works immediately
   */
  const registerSessionWithAmplify = useCallback(
    async (
      session: AmazonCognitoIdentity.CognitoUserSession,
    ): Promise<void> => {
      try {
        const storage = ensureAuthStorage(userPoolConfig);

        // Ensure Amplify is aware of user pool
        (Auth as unknown as AmplifyAuth).userPool =
          new AmazonCognitoIdentity.CognitoUserPool(userPoolConfig);

        const prefix = `CognitoIdentityServiceProvider.${userPoolConfig.ClientId}`;

        // Extract username from token payload with proper typing
        const accessToken =
          session.getAccessToken() as unknown as CognitoTokenWithPayload;
        const idToken =
          session.getIdToken() as unknown as CognitoTokenWithPayload;

        const username =
          accessToken?.payload?.username ||
          idToken?.payload?.['cognito:username'] ||
          '';

        if (!username) {
          throw new Error('No username found in token payload');
        }

        // Persist all tokens to storage
        storage.setItem(`${prefix}.LastAuthUser`, username);
        storage.setItem(
          `${prefix}.${username}.idToken`,
          session.getIdToken().getJwtToken(),
        );
        storage.setItem(
          `${prefix}.${username}.accessToken`,
          session.getAccessToken().getJwtToken(),
        );
        storage.setItem(
          `${prefix}.${username}.refreshToken`,
          session.getRefreshToken().getToken(),
        );

        // Optional: Store token expiration for monitoring
        const exp = session.getAccessToken().getExpiration();
        if (exp) {
          storage.setItem(`${prefix}.${username}.tokenExp`, String(exp));
        }

        // Warm up Amplify caches
        await Auth.currentAuthenticatedUser({ bypassCache: true });
        await Auth.currentSession();
      } catch (e) {
        console.error(
          '[registerSessionWithAmplify] Failed to register session:',
          e,
        );
        throw e;
      }
    },
    [userPoolConfig, ensureAuthStorage],
  );

  /**
   * Performs IdP login with session data
   */
  const idpLogin = useCallback(
    async (sessionData: SSOSessionData): Promise<void> => {
      if (!(sessionData?.AccessToken && sessionData?.IdToken)) {
        return;
      }

      const userPool = new AmazonCognitoIdentity.CognitoUserPool(
        userPoolConfig,
      );
      const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

      // Extract username with proper typing
      const accessToken =
        sessionData.AccessToken as unknown as CognitoTokenWithPayload;
      const idToken = sessionData.IdToken as unknown as CognitoTokenWithPayload;

      const username =
        accessToken?.payload?.username ||
        idToken?.payload?.['cognito:username'] ||
        '';

      if (!username) {
        console.error('[idpLogin] No username found in token payload');
        return;
      }

      const userData = {
        Username: username,
        Pool: userPool,
      };

      const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
      cognitoUser.setSignInUserSession(session);

      // Make the session immediately visible to Amplify (prevents 401 bursts)
      try {
        await registerSessionWithAmplify(session);
      } catch (e) {
        console.warn(
          '[idpLogin] Session registration failed, continuing anyway:',
          e,
        );
        // Continue - Apollo retry link may recover
      }

      // Persist ID token early for any fallbacks that read localStorage
      try {
        const idJwt = session.getIdToken().getJwtToken();
        if (idJwt) {
          saveIdToken(idJwt);
        }
      } catch (e) {
        console.warn('[idpLogin] Unable to persist ID token:', e);
      }

      try {
        const userAttributes = await fetchUserAttributes(cognitoUser);

        // Mark this as an SSO login
        localStorage.setItem('isSSOLogin', 'true');

        // Check setup and terms acceptance status
        const setupAttr = userAttributes['custom:setup_complete'];
        const termsAttr = userAttributes['custom:terms_accepted'];
        const eulaAccepted = localStorage.getItem('isEulaAccepted');

        const isNewUser =
          setupAttr === 'false' ||
          termsAttr === 'false' ||
          eulaAccepted === 'false';

        const cognitoUserWithChallenges =
          cognitoUser as CognitoUserWithChallengeProps;

        // Build authenticated user object with proper string types for attributes
        const cognitoUserWithAttrs: AuthenticatedUser = {
          ...cognitoUser,
          challengeName: cognitoUserWithChallenges.challengeName || '',
          challengeParam: cognitoUserWithChallenges.challengeParam || {},
          attributes: {
            ...userAttributes,
            // Convert booleans to strings as required by the type
            isSSOTestLogin: 'true',
            isNewSSOUser: String(isNewUser),
          },
          preferredMFA: 'SSO',
        };

        setUser?.(cognitoUserWithAttrs);
      } catch (error) {
        console.error('[idpLogin] Failed to fetch user attributes:', error);

        // Clean up on failure
        setUser?.(undefined);
        clearSSOStorage();

        // Optionally, throw to let caller handle
        throw error;
      }
    },
    [
      setUser,
      userPoolConfig,
      fetchUserAttributes,
      registerSessionWithAmplify,
      saveIdToken,
    ],
  );

  /**
   * Checks if the current user is a new SSO user
   */
  const isNewSSOUser = useCallback(
    (): boolean =>
      sessionStorage.getItem('sso_new_user') === 'true' ||
      localStorage.getItem('sso_new_user') === 'true',
    [],
  );

  /**
   * Clears all new SSO user flags from storage
   */
  const clearNewSSOUserFlags = useCallback((): void => {
    sessionStorage.removeItem('sso_new_user');
    localStorage.removeItem('sso_new_user');
  }, []);

  return {
    idpLogin,
    getSessionData,
    checkSsoError,
    isNewSSOUser,
    clearNewSSOUserFlags,
  };
};
